import type { FC } from 'react';
import { useState } from 'react';

interface FAQItem {
  question: string;
  answer: string;
}

const faqItems: FAQItem[] = [
  {
    question: '¿Es gratis registrar mi radio en Radio Frecuencia?',
    answer: 'Sí, el registro básico es completamente gratuito. Podés crear el perfil de tu radio, transmitir en vivo, conectar con oyentes y ser parte del ecosistema social RFW sin costo. Los planes pagos desbloquean más almacenamiento para fotos y videos, estadísticas avanzadas y herramientas de monetización.',
  },
  {
    question: '¿Necesito descargar alguna aplicación?',
    answer: 'No es necesario descargar nada. Radio Frecuencia funciona 100% desde el navegador web en computadoras, tablets y celulares. La experiencia está optimizada para todos los dispositivos sin necesidad de instalar apps. También ofrecemos una versión App mobile con acceso rápido a la comunidad.',
  },
  {
    question: '¿Cómo funciona la transmisión en vivo?',
    answer: 'Tu radio transmite como siempre lo hace, usando tu propio servidor de streaming. Radio Frecuencia se conecta a ese stream y lo reproduce dentro de la plataforma. También podés programar lives con YouTube Live, Facebook Live o nuestro sistema propio.',
  },
  {
    question: '¿Los oyentes pueden interactuar con los locutores?',
    answer: 'Totalmente. Los oyentes pueden enviar mensajes, solicitar canciones, mandar saludos al aire e incluso mensajes de voz directamente desde la página de cada radio. Los locutores reciben todo en su panel de Cabina y pueden responder, destacar o enviar al aire en tiempo real.',
  },
  {
    question: '¿Cómo funcionan las competencias y rankings?',
    answer: 'RFW tiene un sistema de rankings en tiempo real — el RFW Score — que mide la actividad de radios, programas, locutores, clubes y hasta oyentes. Hay competencias tipo Radio vs Radio, torneos, Hall of Fame y premios. No se trata solo de audiencia, sino de comunidad activa: publicaciones, eventos, concursos, interacciones y más.',
  },
  {
    question: '¿Qué son los Clubes de Fans?',
    answer: 'Cada radio, programa y locutor puede tener su Club de Fans. Los clubes tienen su propio espacio con publicaciones, fotos, videos, eventos, concursos, retos y rankings internos. Los miembros ganan insignias, compiten entre clubes y construyen comunidad. Es como tener una red social privada para tus oyentes más fieles.',
  },
  {
    question: '¿Radio Frecuencia tiene anuncios?',
    answer: 'La plataforma muestra anuncios no intrusivos que ayudan a mantener el servicio gratuito. Las radios con planes pagos pueden elegir mostrar menos anuncios o incluso desactivarlos completamente. También ofrecemos espacios publicitarios y un marketplace para que marcas se anuncien en las páginas de las radios.',
  },
  {
    question: '¿Cuántas frecuencias RFW hay disponibles?',
    answer: 'En esta etapa de lanzamiento hay solo 11,300 frecuencias RFW disponibles. Las frecuencias son limitadas y se asignan por orden de aprobación. Una vez ocupada una frecuencia, queda reservada para esa emisora mientras mantenga su cuenta activa y cumpla con las políticas de la plataforma.',
  },
  {
    question: '¿Mi radio necesita ser aprobada para aparecer en la plataforma?',
    answer: 'Sí, cada emisora debe ser aprobada antes de formar parte del ecosistema RFW. No todas las solicitudes son aceptadas. Nuestro equipo revisa la calidad del stream, la propuesta de contenido y que la radio tenga una presencia real. Buscamos construir una red social de emisoras activas y comprometidas con sus oyentes.',
  },
  {
    question: '¿Qué recibe mi radio al ser aprobada?',
    answer: 'Al ser aprobada, tu estación entra al ecosistema social de RFW y recibe: frecuencia RFW exclusiva, página pública profesional, cabina interactiva con chat en vivo, código QR personalizado, club de fans automático, sistema de seguidores e interacciones, participación en rankings y competencias, perfil verificable con insignia de autenticidad, inclusión en el directorio oficial, y acceso al marketplace publicitario y de patrocinios.',
  },
  {
    question: '¿Puedo tener mi propio dominio personalizado?',
    answer: 'En el plan Premium podés conectar tu propio dominio personalizado para que tu perfil de radio tenga una URL profesional como "tupagina.miradio.com". Los planes Básico y Pro usan subdominios de radiofrecuenciaweb.com.',
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold mb-4">
            <i className="ri-question-line text-xs"></i>
            Preguntas Frecuentes
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
            Todo lo que necesitás{' '}
            <span className="text-primary-600">saber</span>
          </h2>
          <p className="mt-3 text-foreground-500 text-base max-w-xl mx-auto">
            Respuestas claras a las preguntas más comunes sobre la plataforma.
          </p>
        </div>

        <div className="max-w-3xl mx-auto space-y-3">
          {faqItems.map((item, i) => {
            const isOpen = openIndex === i;
            return (
              <div
                key={i}
                className={`rounded-2xl border transition-all duration-300 ${
                  isOpen
                    ? 'bg-background-50 border-primary-300 shadow-md shadow-primary-500/5'
                    : 'bg-background-100 border-background-200 hover:border-primary-200'
                }`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between gap-4 px-5 md:px-6 py-4 md:py-5 text-left cursor-pointer"
                >
                  <h4 className="text-sm md:text-base font-semibold text-foreground-900 pr-2">
                    {item.question}
                  </h4>
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-all duration-300 ${
                      isOpen
                        ? 'bg-primary-500 text-background-50 rotate-45'
                        : 'bg-background-200 text-foreground-500'
                    }`}
                  >
                    <i className="ri-add-line text-sm"></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <p className="px-5 md:px-6 pb-5 text-sm text-foreground-600 leading-relaxed">
                    {item.answer}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Schema.org FAQPage structured data */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.answer,
                },
              })),
            }),
          }}
        />
      </div>
    </section>
  );
}